import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import './UserBadgesPage.css';
import { localizePlace, localizeLocation } from './utils/places';
import MythicLogo from './assets/logos/Mythic_Logo.png';
import JapaneseLogo from './assets/logos/Japanese_Logo.png';
import AztecCodexLogo from './assets/logos/Aztec_Codex_Logo.png';
import UrbanViceLogo from './assets/logos/Urban_Vice_Logo.png';
import ClassicRTSLogo from './assets/logos/Classic_RTS_Logo.png';
import TourDeFrance80sLogo from './assets/logos/80ies_TdF_Logo.png';
import ModernVueltaLogo from './assets/logos/Modern_Vuelta_Logo.png';
import AboriginalSonglineLogo from './assets/logos/Aboriginal_Songline_Logo.png';
import CaravanAtlasLogo from './assets/logos/Caravan_Atlas_Logo.png';

const UserBadgesPage = ({ user }) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [allBadges, setAllBadges] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [sortBy, setSortBy] = useState('alphabetical'); // 'alphabetical' or 'date'
    const [selectedBadge, setSelectedBadge] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showCollectionModal, setShowCollectionModal] = useState(false);
    const [totalBadges, setTotalBadges] = useState(0);
    const [earnedBadges, setEarnedBadges] = useState(0);
    const [completedSets, setCompletedSets] = useState([]);
    const [expandedLocationSections, setExpandedLocationSections] = useState(new Set());
    const [hasInitialized, setHasInitialized] = useState(false);
    const [showIntroAnimation, setShowIntroAnimation] = useState(false);
    const [hoveredBadgeId, setHoveredBadgeId] = useState(null);
    const [unhoveringBadgeId, setUnhoveringBadgeId] = useState(null);
    
    useEffect(() => {
        if (!user) {
            navigate('/');
            return;
        }
        
        loadAllBadges();
    }, [user, navigate]);
    
    const loadAllBadges = async () => {
        setIsLoading(true);
        try {
            // Get all badges with progress information
            const getAllBadgesWithProgress = httpsCallable(functions, 'getAllBadgesWithProgress');
            const allBadgesResult = await getAllBadgesWithProgress();
            const allBadgesData = allBadgesResult.data.badges || [];
            
            // Get user's earned badges for detailed information
            const getUserBadges = httpsCallable(functions, 'getUserBadges');
            const userBadgesResult = await getUserBadges();
            const earnedBadgesList = userBadgesResult.data.badges || [];
            
            // Create a map of earned badges for quick lookup
            const earnedBadgesMap = new Map();
            earnedBadgesList.forEach(badge => {
                const key = `${badge.badgeId || badge.id}_${badge.styleName || 'default'}`;
                if (!earnedBadgesMap.has(key)) {
                    earnedBadgesMap.set(key, []);
                }
                earnedBadgesMap.get(key).push(badge);
            });
            
            // Process all badges and create entries for each style version
            const processedBadges = [];
            
            // Dynamically extract available styles from badge data
            const availableStylesSet = new Set(['Mythic']); // Always include Mythic as default
            allBadgesData.forEach(badge => {
                if (badge.styleVersions) {
                    Object.keys(badge.styleVersions).forEach(styleName => {
                        availableStylesSet.add(styleName);
                    });
                }
            });
            const availableStyles = Array.from(availableStylesSet);
            
            allBadgesData.forEach(badge => {
                // For each badge, create entries for all available styles
                availableStyles.forEach(styleName => {
                    // Check if this badge has this style version
                    let imageUrl = badge.imageUrl; // default image
                    let size = badge.size || 15;
                    
                    if (badge.styleVersions && badge.styleVersions[styleName]) {
                        imageUrl = badge.styleVersions[styleName].imageUrl;
                        if (badge.styleVersions[styleName].size !== undefined) {
                            size = badge.styleVersions[styleName].size || size;
                        }
                    } else if (styleName !== 'Mythic') {
                        // Skip this style if it doesn't exist for this badge and it's not the default style
                        return;
                    }
                    
                    // Check if this specific badge+style combination is earned
                    const earnedKey = `${badge.id}_${styleName}`;
                    const earnedInstances = earnedBadgesMap.get(earnedKey) || [];
                    const isEarned = earnedInstances.length > 0;
                    
                    // Calculate first/last earned dates and activities
                    let firstEarned = null;
                    let lastEarned = null;
                    const activities = [];
                    
                    if (isEarned) {
                        earnedInstances.forEach(instance => {
                            activities.push({
                                activityName: instance.activityName,
                                activityDate: instance.activityDate,
                                awardedAt: instance.awardedAt,
                                generationRequestId: instance.generationRequestId
                            });
                            
                            const currentTime = instance.awardedAt?._seconds || instance.awardedAt?.seconds || 0;
                            if (!firstEarned || currentTime < (firstEarned._seconds || firstEarned.seconds || 0)) {
                                firstEarned = instance.awardedAt;
                            }
                            if (!lastEarned || currentTime > (lastEarned._seconds || lastEarned.seconds || 0)) {
                                lastEarned = instance.awardedAt;
                            }
                        });
                    }
                    
                    processedBadges.push({
                        id: `${badge.id}_${styleName}`,
                        badgeId: badge.id,
                        name: badge.name,
                        imageUrl: imageUrl,
                        styleName: styleName,
                        size: size,
                        conditionType: badge.conditionType,
                        distanceThreshold: badge.distanceThreshold,
                        activityType: badge.activityType,
                        locationCity: badge.locationCity,
                        locationState: badge.locationState,
                        locationCountry: badge.locationCountry,
                        isEarned: isEarned,
                        earnedCount: earnedInstances.length,
                        firstEarned: firstEarned,
                        lastEarned: lastEarned,
                        activities: activities
                    });
                });
            });
            
            setAllBadges(processedBadges);
            setTotalBadges(processedBadges.length);
            setEarnedBadges(processedBadges.filter(b => b.isEarned).length);
            
            // Set initialized after a short delay to trigger initial animation
            setTimeout(() => {
                setHasInitialized(true);
                // Calculate delay based on number of earned badges
                // Each badge has 0.05s delay + 0.4s animation duration
                const earnedCount = processedBadges.filter(b => b.isEarned).length;
                const totalAnimationTime = 400 + (earnedCount * 50); // slideIn duration + stagger delays
                
                // Start intro animation after all badges complete their slideIn
                setTimeout(() => {
                    setShowIntroAnimation(true);
                    // Stop intro animation after 2 seconds
                    setTimeout(() => {
                        setShowIntroAnimation(false);
                    }, 2000);
                }, totalAnimationTime + 200); // Add 200ms buffer
            }, 500);
        } catch (error) {
            console.error('Error loading badges:', error);
            setAllBadges([]);
            setTotalBadges(0);
            setEarnedBadges(0);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Reload badges when filter changes
    useEffect(() => {
        if (!isLoading && user) {
            loadAllBadges();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilter]);
    
    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        
        // Handle Firestore timestamp format with _seconds
        if (timestamp._seconds) {
            const date = new Date(timestamp._seconds * 1000);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        }
        
        // Handle regular Firebase Timestamp with toDate method
        if (timestamp.toDate) {
            return timestamp.toDate().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        }
        
        return '';
    };
    
    const getDistanceDisplay = (threshold) => {
        if (!threshold) return '';
        const km = threshold / 1000;
        return `${km}km`;
    };

    const getLocationDisplay = (badge) => {
        return localizeLocation({
            city: badge.locationCity,
            state: badge.locationState,
            country: badge.locationCountry
        }, i18n?.language);
    };

    const getBadgeTypeLabel = (badge) => {
        if (badge.conditionType === 'distance') {
            return 'Distance';
        } else if (badge.conditionType === 'location') {
            return 'Location';
        }
        return 'Unknown';
    };

    const getAvailableOptions = () => {
        // Dynamically extract all styles from badges
        const allStyles = new Set();
        allBadges.forEach(badge => {
            if (badge.styleName) {
                allStyles.add(badge.styleName);
            }
        });
        const allStylesArray = Array.from(allStyles).sort();
        
        const allCities = ['Seattle', 'Shanghai', 'Tokyo', 'Kuala Lumpur', 'Sydney', 'Paris', 'Los Angeles', 'Ningbo', 'Fukuoka', 'Beijing'];
        
        const allCountries = ['China', 'USA', 'Hong Kong', 'Japan', 'Singapore', 'UK', 'Malaysia', 'Germany', 'Canada', 'France'];
        
        const allDistances = ['10km', '30km', '50km', '100km', '200km'];
        
        // Create sets of what user has earned
        const earnedStyles = new Set();
        const earnedLocations = new Set();
        const earnedDistances = new Set();
        
        allBadges.forEach(badge => {
            if (badge.styleName) {
                earnedStyles.add(badge.styleName);
            }
            
            if (badge.conditionType === 'location') {
                if (badge.locationCity && badge.locationCountry) {
                    earnedLocations.add(`${badge.locationCity}, ${badge.locationCountry}`);
                } else if (badge.locationCountry) {
                    earnedLocations.add(badge.locationCountry);
                }
            }
            
            if (badge.conditionType === 'distance' && badge.distanceThreshold) {
                const km = badge.distanceThreshold / 1000;
                earnedDistances.add(`${km}km`);
            }
        });
        
        // Create grouped options
        const options = [
            { value: 'all', label: 'All Badges', group: 'general', earned: true }
        ];
        
        // Add style options
        allStylesArray.forEach(style => {
            options.push({
                value: `style:${style}`,
                label: style,
                group: 'style',
                earned: earnedStyles.has(style)
            });
        });
        
        // Add city locations
        allCities.forEach(city => {
            // Try to match with country info from earned badges
            let fullLocation = city;
            const matchingBadge = Array.from(allBadges).find(badge => 
                badge.locationCity === city || badge.locationCity === city.replace(' ', '')
            );
            if (matchingBadge && matchingBadge.locationCountry) {
                fullLocation = `${city}, ${matchingBadge.locationCountry}`;
            }
            
            options.push({
                value: `location:${fullLocation}`,
                label: localizePlace(city, i18n?.language) + (matchingBadge?.locationCountry ? `${i18n?.language?.startsWith('zh') ? '，' : ', '}${localizePlace(matchingBadge.locationCountry, i18n?.language)}` : ''),
                group: 'city',
                earned: earnedLocations.has(fullLocation) || earnedLocations.has(city)
            });
        });
        
        // Add country locations
        allCountries.forEach(country => {
            options.push({
                value: `location:${country}`,
                label: localizePlace(country, i18n?.language),
                group: 'country',
                earned: earnedLocations.has(country)
            });
        });
        
        // Add distance options
        allDistances.forEach(distance => {
            options.push({
                value: `distance:${distance}`,
                label: distance,
                group: 'distance',
                earned: earnedDistances.has(distance)
            });
        });
        
        return options;
    };

    const getFilteredBadges = () => {
        let filtered = [...allBadges];
        
        // Apply filter
        if (selectedFilter !== 'all') {
            const [filterType, filterValue] = selectedFilter.split(':');
            
            if (filterType === 'style') {
                filtered = filtered.filter(badge => badge.styleName === filterValue);
            } else if (filterType === 'location') {
                filtered = filtered.filter(badge => {
                    const badgeLocation = badge.locationCity && badge.locationCountry 
                        ? `${badge.locationCity}, ${badge.locationCountry}`
                        : badge.locationCountry;
                    return badgeLocation === filterValue;
                });
            } else if (filterType === 'distance') {
                const filterKm = parseFloat(filterValue);
                filtered = filtered.filter(badge => {
                    const badgeKm = badge.distanceThreshold / 1000;
                    return badgeKm === filterKm;
                });
            }
        }
        
        // Group by style name and then by badge type
        const grouped = {};
        filtered.forEach(badge => {
            if (!grouped[badge.styleName]) {
                grouped[badge.styleName] = {
                    distance: [],
                    locationCountry: [],
                    locationCity: []
                };
            }
            
            // Separate by condition type
            if (badge.conditionType === 'distance') {
                grouped[badge.styleName].distance.push(badge);
            } else if (badge.conditionType === 'location') {
                // Further separate location badges by country vs city
                if (badge.locationCity) {
                    grouped[badge.styleName].locationCity.push(badge);
                } else {
                    grouped[badge.styleName].locationCountry.push(badge);
                }
            }
        });
        
        // Sort badges within each type
        Object.keys(grouped).forEach(styleName => {
            // Sort distance badges by distance value only
            grouped[styleName].distance.sort((a, b) => {
                // Sort by distance threshold (10k, 30k, 50k, 100k, etc.)
                const distA = a.distanceThreshold || 0;
                const distB = b.distanceThreshold || 0;
                return distA - distB;
            });
            
            // Sort country location badges
            grouped[styleName].locationCountry.sort((a, b) => {
                // First, sort earned badges before unearned
                if (a.isEarned && !b.isEarned) return -1;
                if (!a.isEarned && b.isEarned) return 1;
                
                // Then apply the selected sort
                if (sortBy === 'alphabetical') {
                    return a.name.localeCompare(b.name);
                } else if (sortBy === 'date') {
                    // Sort by first earned date for earned badges
                    if (a.isEarned && b.isEarned) {
                        const aTime = a.firstEarned?._seconds || a.firstEarned?.seconds || 0;
                        const bTime = b.firstEarned?._seconds || b.firstEarned?.seconds || 0;
                        return bTime - aTime; // Most recent first
                    }
                    // For unearned badges, use alphabetical
                    return a.name.localeCompare(b.name);
                }
                return 0;
            });
            
            // Sort city location badges
            grouped[styleName].locationCity.sort((a, b) => {
                // First, sort earned badges before unearned
                if (a.isEarned && !b.isEarned) return -1;
                if (!a.isEarned && b.isEarned) return 1;
                
                // Then apply the selected sort
                if (sortBy === 'alphabetical') {
                    return a.name.localeCompare(b.name);
                } else if (sortBy === 'date') {
                    // Sort by first earned date for earned badges
                    if (a.isEarned && b.isEarned) {
                        const aTime = a.firstEarned?._seconds || a.firstEarned?.seconds || 0;
                        const bTime = b.firstEarned?._seconds || b.firstEarned?.seconds || 0;
                        return bTime - aTime; // Most recent first
                    }
                    // For unearned badges, use alphabetical
                    return a.name.localeCompare(b.name);
                }
                return 0;
            });
        });
        
        return grouped;
    };

    const handleBadgeClick = (badge) => {
        setSelectedBadge(badge);
        setShowModal(true);
    };

    const handleMapClick = (generationRequestId) => {
        navigate(`/map/${generationRequestId}`);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedBadge(null);
    };

    const handleCollectionClick = () => {
        calculateCompletedSets();
        setShowCollectionModal(true);
    };

    const closeCollectionModal = () => {
        setShowCollectionModal(false);
    };


    const toggleFilterMenu = () => {
        setShowFilterMenu(!showFilterMenu);
    };

    const closeFilterMenu = () => {
        setShowFilterMenu(false);
    };
    
    const toggleLocationSection = (styleName) => {
        const newExpanded = new Set(expandedLocationSections);
        if (newExpanded.has(styleName)) {
            newExpanded.delete(styleName);
        } else {
            newExpanded.add(styleName);
        }
        setExpandedLocationSections(newExpanded);
    };

    const handleFilterChange = (filterValue) => {
        setSelectedFilter(filterValue);
        setShowFilterMenu(false);
    };

    const handleSortChange = (sortValue) => {
        setSortBy(sortValue);
    };

    const calculateCompletedSets = () => {
        // Group badges by badge ID to check completion
        const badgeGroups = new Map();
        allBadges.forEach(badge => {
            if (!badgeGroups.has(badge.badgeId)) {
                badgeGroups.set(badge.badgeId, {
                    name: badge.name,
                    location: `${badge.locationCity}, ${badge.locationState || badge.locationCountry}`,
                    styles: new Set(),
                    conditionType: badge.conditionType
                });
            }
            badgeGroups.get(badge.badgeId).styles.add(badge.styleName);
        });

        // Group by style to check if all badges in a style are collected
        const styleGroups = new Map();
        allBadges.forEach(badge => {
            if (!styleGroups.has(badge.styleName)) {
                styleGroups.set(badge.styleName, new Set());
            }
            styleGroups.get(badge.styleName).add(badge.badgeId);
        });

        const completed = [];

        // Check for completed badge sets (all styles of a specific badge)
        // Dynamically determine total available styles
        const totalAvailableStyles = new Set();
        allBadges.forEach(badge => {
            if (badge.styleName) {
                totalAvailableStyles.add(badge.styleName);
            }
        });
        const totalStyleCount = totalAvailableStyles.size;
        
        badgeGroups.forEach((group, badgeId) => {
            // Check if user has collected all available styles for this badge
            if (group.styles.size >= totalStyleCount) {
                completed.push({
                    type: 'badge_complete',
                    name: group.name,
                    description: `All styles collected for ${group.location}`,
                    count: group.styles.size
                });
            }
        });

        // Check for completed style sets (all badges in a specific style)
        styleGroups.forEach((badges, styleName) => {
            // This would need to be compared against total possible badges in that style
            // For now, let's say if someone has 5+ badges in a style, it's a significant collection
            if (badges.size >= 5) {
                completed.push({
                    type: 'style_complete',
                    name: `${styleName} Collection`,
                    description: `${badges.size} badges collected in ${styleName} style`,
                    count: badges.size
                });
            }
        });

        setCompletedSets(completed);
    };
    
    const groupedBadges = getFilteredBadges();
    const availableOptions = getAvailableOptions();
    const hasAnyBadges = Object.keys(groupedBadges).length > 0;
    
    const styleLogos = {
        'Mythic': MythicLogo,
        'Japanese': JapaneseLogo,
        'Aztec Codex': AztecCodexLogo,
        'Urban Vice': UrbanViceLogo,
        'Classic RTS': ClassicRTSLogo,
        '80ies Tour de France': TourDeFrance80sLogo,
        'Modern Vuelta': ModernVueltaLogo,
        'Aboriginal Songline': AboriginalSonglineLogo,
        'Caravan Atlas': CaravanAtlasLogo
    };
    
    // Create a tiny deterministic offset for each badge so the board looks less neat
    const getOffsetForBadge = (badgeKey) => {
        const str = String(badgeKey || '');
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = ((hash << 5) - hash) + str.charCodeAt(i);
            hash |= 0; // Convert to 32bit int
        }
        const rand1 = ((hash % 11) + 11) % 11; // 0..10
        const rand2 = ((Math.floor(hash / 11) % 11) + 11) % 11; // 0..10
        return { offsetX: (rand1 - 5) * 2.5, offsetY: (rand2 - 5) * 2.5 }; // -20..20 each
    };

    // Provide a deterministic initial angle per badge so they aren't all perfectly straight
    const getAngleForBadge = (badgeKey) => {
        // Start at 0 degrees for all badges
        return 0;
    };
    
    // Generate a random final angle for each badge after intro animation
    const getFinalAngleForBadge = (badgeKey) => {
        const str = String(badgeKey || '');
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = ((hash << 5) - hash) + str.charCodeAt(i);
            hash |= 0; // Convert to 32bit int
        }
        // Generate a deterministic "random" angle between -6 and +6 degrees
        const rand = ((hash % 13) + 13) % 13; // 0..12
        return rand - 6; // -6..6 degrees
    };
    
    const handleBadgeHover = (badgeId) => {
        setHoveredBadgeId(badgeId);
        setUnhoveringBadgeId(null);
    };
    
    const handleBadgeUnhover = (badgeId) => {
        setHoveredBadgeId(null);
        setUnhoveringBadgeId(badgeId);
        
        // Clear the unhovering state after animation completes
        setTimeout(() => {
            setUnhoveringBadgeId(prev => prev === badgeId ? null : prev);
        }, 1500); // Match the duration of the decay animation
    };
    
    return (
        <div className="user-badges-page">
            <div className="page-header">
                <div className="header-content">
                    <h1>{t('badgesPage.title', 'Mythic Badges')}</h1>
                    <p>{t('badgesPage.subtitle', 'Explore the Mythic multiverse and collect unique swag')}</p>
                </div>
            </div>
            
            {/* Progress moved onto cork board; summary card removed */}

            
            
            {isLoading ? (
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>{t('badgesPage.loading', 'Loading badge collection...')}</p>
                </div>
            ) : allBadges.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">🏆</div>
                    <h3>{t('badgesPage.noBadgesTitle', 'No badges earned yet')}</h3>
                    <p>{t('badgesPage.noBadgesDesc', 'Create some maps from your Strava activities to start earning badges!')}</p>
                </div>
            ) : !hasAnyBadges ? (
                <div className="empty-state">
                    <div className="empty-icon">🔍</div>
                    <h3>{t('badgesPage.noMatchTitle', 'No badges match your filter')}</h3>
                    <p>{t('badgesPage.noMatchDesc', 'Try selecting a different filter or clear your current selection.')}</p>
                </div>
            ) : (
                <>
                    {/* Cork Board for Earned Badges */}
                    {earnedBadges > 0 && (
                        <div className="cork-board-section">
                          
                            <div className="cork-board">
                                <div 
                                    className="cork-progress" 
                                    title={t('badgesPage.collectionProgress', 'Collection Progress')}
                                >
                                    {earnedBadges} / {totalBadges} 
                                <div>Badges</div>
                                </div>
                                <div className="cork-board-badges">
                                    {allBadges.filter(badge => badge.isEarned).map((badge, index) => {
                                        const angle = getAngleForBadge(badge.id);
                                        const finalAngle = getFinalAngleForBadge(badge.id);
                                        const { offsetX, offsetY } = getOffsetForBadge(badge.id);
                                        
                                        return (
                                            <div 
                                                key={badge.id} 
                                                className={`cork-board-badge ${showIntroAnimation ? 'animate-intro' : ''} ${hoveredBadgeId === badge.id ? 'is-hovering' : ''} ${unhoveringBadgeId === badge.id ? 'is-unhovering' : ''}`}
                                                onClick={() => handleBadgeClick(badge)}
                                                onMouseEnter={() => handleBadgeHover(badge.id)}
                                                onMouseLeave={() => handleBadgeUnhover(badge.id)}
                                                style={{
                                                    '--initial-angle': `${angle}deg`,
                                                    '--final-angle': `${finalAngle}deg`,
                                                    '--offsetX': `${offsetX}px`,
                                                    '--offsetY': `${offsetY}px`
                                                }}
                                            >
                                            <div className="push-pin"></div>
                                            <img 
                                                src={badge.imageUrl} 
                                                alt={badge.name}
                                                className="cork-badge-image"
                                            />
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Filter/Sort controls (moved below cork board) */}
                    <div className="filters-section">
                        <div className="filter-controls">
                            <button 
                                className="filter-toggle-button"
                                onClick={toggleFilterMenu}
                                aria-label="Filter and sort options"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 6h18M7 12h10m-7 6h4" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            
                            {showFilterMenu && (
                                <div className="filter-menu">
                                    <div className="filter-menu-header">
                                        <h3>{t('badgesPage.filterSort', 'Filter & Sort')}</h3>
                                        <button className="close-menu" onClick={closeFilterMenu}>×</button>
                                    </div>
                                    
                                    <div className="filter-section">
                                        <h4>{t('badgesPage.sortBy', 'Sort by')}</h4>
                                        <div className="sort-options">
                                            <label className="sort-option">
                                                <input 
                                                    type="radio" 
                                                    name="sort" 
                                                    value="alphabetical" 
                                                    checked={sortBy === 'alphabetical'} 
                                                    onChange={(e) => handleSortChange(e.target.value)}
                                                />
                                                <span>{t('badgesPage.alphabetical', 'Alphabetical')}</span>
                                            </label>
                                            <label className="sort-option">
                                                <input 
                                                    type="radio" 
                                                    name="sort" 
                                                    value="date" 
                                                    checked={sortBy === 'date'} 
                                                    onChange={(e) => handleSortChange(e.target.value)}
                                                />
                                                <span>{t('badgesPage.firstDate', 'First Date Earned')}</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="filter-section">
                                        <h4>{t('badgesPage.filterBy', 'Filter by')}</h4>
                                        
                                        {/* General Option */}
                                        <div className="filter-category">
                                            <div className="filter-options">
                                                {availableOptions.filter(option => option.group === 'general').map((option) => (
                                                    <button
                                                        key={option.value}
                                                        className={`filter-option ${selectedFilter === option.value ? 'active' : ''} ${option.group} ${option.earned ? 'earned' : 'unearned'}`}
                                                        onClick={() => handleFilterChange(option.value)}
                                                    >
                                                        <div className="filter-label">
                                                            {option.earned && <span className="earned-indicator">✓</span>}
                                                            {option.label}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Style Section */}
                                        <div className="filter-category">
                                            <h5 className="filter-category-title">{t('badgesPage.style', 'Style')}</h5>
                                            <div className="filter-options">
                                                {availableOptions.filter(option => option.group === 'style').map((option) => (
                                                    <button
                                                        key={option.value}
                                                        className={`filter-option ${selectedFilter === option.value ? 'active' : ''} ${option.group} ${option.earned ? 'earned' : 'unearned'}`}
                                                        onClick={() => handleFilterChange(option.value)}
                                                    >
                                                        <div className="filter-label">
                                                            {option.earned && <span className="earned-indicator">✓</span>}
                                                            {option.label}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Distance Section */}
                                        <div className="filter-category">
                                            <h5 className="filter-category-title">{t('badgesPage.distance', 'Distance')}</h5>
                                            <div className="filter-options">
                                                {availableOptions.filter(option => option.group === 'distance').map((option) => (
                                                    <button
                                                        key={option.value}
                                                        className={`filter-option ${selectedFilter === option.value ? 'active' : ''} ${option.group} ${option.earned ? 'earned' : 'unearned'}`}
                                                        onClick={() => handleFilterChange(option.value)}
                                                    >
                                                        <div className="filter-label">
                                                            {option.earned && <span className="earned-indicator">✓</span>}
                                                            {option.label}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Cities Section */}
                                        <div className="filter-category">
                                            <h5 className="filter-category-title">{t('badgesPage.cities', 'Cities')}</h5>
                                            <div className="filter-options">
                                                {availableOptions.filter(option => option.group === 'city').map((option) => (
                                                    <button
                                                        key={option.value}
                                                        className={`filter-option ${selectedFilter === option.value ? 'active' : ''} ${option.group} ${option.earned ? 'earned' : 'unearned'}`}
                                                        onClick={() => handleFilterChange(option.value)}
                                                    >
                                                        <div className="filter-label">
                                                            {option.earned && <span className="earned-indicator">✓</span>}
                                                            {option.label}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Countries Section */}
                                        <div className="filter-category">
                                            <h5 className="filter-category-title">{t('badgesPage.countries', 'Countries')}</h5>
                                            <div className="filter-options">
                                                {availableOptions.filter(option => option.group === 'country').map((option) => (
                                                    <button
                                                        key={option.value}
                                                        className={`filter-option ${selectedFilter === option.value ? 'active' : ''} ${option.group} ${option.earned ? 'earned' : 'unearned'}`}
                                                        onClick={() => handleFilterChange(option.value)}
                                                    >
                                                        <div className="filter-label">
                                                            {option.earned && <span className="earned-indicator">✓</span>}
                                                            {option.label}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Existing Badges by Style */}
                    <div className="badges-by-style">
                        {Object.entries(groupedBadges).map(([styleName, badgesByType]) => {
                        const hasDistanceBadges = badgesByType.distance && badgesByType.distance.length > 0;
                        const hasCountryBadges = badgesByType.locationCountry && badgesByType.locationCountry.length > 0;
                        const hasCityBadges = badgesByType.locationCity && badgesByType.locationCity.length > 0;
                        const isCountryExpanded = expandedLocationSections.has(`${styleName}-country`);
                        const isCityExpanded = expandedLocationSections.has(`${styleName}-city`);
                        const earnedCountryCount = badgesByType.locationCountry.filter(b => b.isEarned).length;
                        const earnedCityCount = badgesByType.locationCity.filter(b => b.isEarned).length;
                        
                        return (
                            <div key={styleName} className="style-group">
                                <div className="style-header-card">
                                    {styleLogos[styleName] && (
                                        <img className="style-logo" src={styleLogos[styleName]} alt={`${styleName} logo`} />
                                    )}
                                    <h2 className="style-group-title">{styleName}</h2>
                                </div>
                                
                                {/* Distance Badges */}
                                {hasDistanceBadges && (() => {
                                    const distanceKey = `${styleName}-distance`;
                                    const isDistanceExpanded = !expandedLocationSections.has(distanceKey);
                                    const earnedDistanceCount = badgesByType.distance.filter(b => b.isEarned).length;
                                    return (
                                        <div className="badge-type-section location-section">
                                            <button 
                                                className="badge-type-title collapsible"
                                                onClick={() => toggleLocationSection(distanceKey)}
                                                aria-expanded={isDistanceExpanded}
                                            >
                                                <span className="title-text">
                                                    {t('badgesPage.distanceBadges', 'Distance Badges')}
                                                    <span className="badge-count">({earnedDistanceCount}/{badgesByType.distance.length})</span>
                                                    <span className={`expand-icon ${isDistanceExpanded ? 'expanded' : ''}`}>▶</span>
                                                </span>
                                            </button>
                                            {isDistanceExpanded && (
                                                <div className="badges-grid">
                                                    {badgesByType.distance.map((badge) => (
                                                        <div 
                                                            key={badge.id} 
                                                            className={`badge-image-item ${!badge.isEarned ? 'unearned' : ''}`}
                                                            onClick={() => handleBadgeClick(badge)}
                                                        >
                                                            <img 
                                                                src={badge.imageUrl} 
                                                                alt={badge.name}
                                                                className="badge-image"
                                                                style={{ opacity: badge.isEarned ? 1 : 0.3 }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                                
                                {/* Country Badges - Collapsible */}
                                {hasCountryBadges && (
                                    <div className="badge-type-section location-section">
                                        <button 
                                            className="badge-type-title collapsible"
                                            onClick={() => toggleLocationSection(`${styleName}-country`)}
                                            aria-expanded={isCountryExpanded}
                                        >
                                            <span className="title-text">
                                                {t('badgesPage.countryBadges', 'Country Badges')}
                                                <span className="badge-count">({earnedCountryCount}/{badgesByType.locationCountry.length})</span>
                                                <span className={`expand-icon ${isCountryExpanded ? 'expanded' : ''}`}>▶</span>
                                            </span>
                                        </button>
                                        {isCountryExpanded && (
                                            <div className="badges-grid">
                                                {badgesByType.locationCountry.map((badge) => (
                                                    <div 
                                                        key={badge.id} 
                                                        className={`badge-image-item ${!badge.isEarned ? 'unearned' : ''}`}
                                                        onClick={() => handleBadgeClick(badge)}
                                                    >
                                                        <img 
                                                            src={badge.imageUrl} 
                                                            alt={badge.name}
                                                            className="badge-image"
                                                            style={{ opacity: badge.isEarned ? 1 : 0.3 }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {/* City Badges - Collapsible */}
                                {hasCityBadges && (
                                    <div className="badge-type-section location-section">
                                        <button 
                                            className="badge-type-title collapsible"
                                            onClick={() => toggleLocationSection(`${styleName}-city`)}
                                            aria-expanded={isCityExpanded}
                                        >
                                            <span className="title-text">
                                                {t('badgesPage.cityBadges', 'City Badges')}
                                                <span className="badge-count">({earnedCityCount}/{badgesByType.locationCity.length})</span>
                                                <span className={`expand-icon ${isCityExpanded ? 'expanded' : ''}`}>▶</span>
                                            </span>
                                        </button>
                                        {isCityExpanded && (
                                            <div className="badges-grid">
                                                {badgesByType.locationCity.map((badge) => (
                                                    <div 
                                                        key={badge.id} 
                                                        className={`badge-image-item ${!badge.isEarned ? 'unearned' : ''}`}
                                                        onClick={() => handleBadgeClick(badge)}
                                                    >
                                                        <img 
                                                            src={badge.imageUrl} 
                                                            alt={badge.name}
                                                            className="badge-image"
                                                            style={{ opacity: badge.isEarned ? 1 : 0.3 }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    </div>
                </>
            )}

            {/* Badge Detail Modal */}
            {showModal && selectedBadge && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeModal}>×</button>
                        
                        <div className="modal-header">
                            <div className="modal-badge-image">
                                <img 
                                    src={selectedBadge.imageUrl} 
                                    alt={selectedBadge.name}
                                    style={{ opacity: selectedBadge.isEarned ? 1 : 0.3 }}
                                />
                            </div>
                        </div>
                        
                        <div className="modal-info">
                            <h2>{localizePlace(selectedBadge.name, i18n?.language)}</h2>
                            <div className="badge-type-large">{t('badgesPage.badgeType', '{{type}} Badge', { type: getBadgeTypeLabel(selectedBadge) })}</div>
                            
                            <div className="badge-requirement-large">
                                {selectedBadge.conditionType === 'distance' ? (
                                    <span>{t('badgesPage.reqDistance', 'Complete a {{dist}} activity', { dist: getDistanceDisplay(selectedBadge.distanceThreshold) })}</span>
                                ) : (
                                    <span>{t('badgesPage.reqLocation', 'Complete a {{style}} activity in {{where}}', { style: localizePlace(selectedBadge.styleName, i18n?.language), where: getLocationDisplay(selectedBadge) })}</span>
                                )}
                            </div>
                        </div>

                        <div className="modal-body">
                            {selectedBadge.isEarned ? (
                                <div className="earned-details">
                                    {selectedBadge.activities && selectedBadge.activities.length > 0 && (
                                        <div className="activities-section">
                                            <h3>{t('badgesPage.earnedDuring', 'Earned during:')}</h3>
                                            <ul className="activities-bullet-list">
                                                {selectedBadge.activities.map((activity, idx) => (
                                                    <li key={idx} className="activity-bullet-item">
                                                        <button 
                                                            className="activity-name-link"
                                                            onClick={() => handleMapClick(activity.generationRequestId)}
                                                             title={t('badgesPage.viewMap', 'View this map')}
                                                        >
                                                            {activity.activityName}
                                                        </button>
                                                        <span className="activity-date">{formatDate(activity.activityDate)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            </div>
                                    )}
                                </div>
                            ) : (
                                <div className="unearned-details">
                                    <div className="challenge-message">
                                        <h3>🎯 {t('badgesPage.challengeTitle', 'Challenge Awaits!')}</h3>
                                        <p>{t('badgesPage.challengeDesc', 'Complete the requirement above to earn this badge and add it to your collection.')}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Collection Modal */}
            {showCollectionModal && (
                <div className="modal-overlay" onClick={closeCollectionModal}>
                    <div className="modal-content collection-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeCollectionModal}>×</button>
                        
                        <div className="collection-header">
                            <h2>🏆 {t('badgesPage.completedSets', 'Completed Sets')}</h2>
                            <p>{t('badgesPage.completedSubtitle', "Badge collections you've mastered")}</p>
                        </div>

                        <div className="collection-body">
                            {completedSets.length === 0 ? (
                                <div className="no-sets">
                                    <div className="no-sets-icon">📦</div>
                                    <h3>{t('badgesPage.noSetsTitle', 'No completed sets yet')}</h3>
                                    <p>{t('badgesPage.noSetsDesc', 'Complete all styles of a badge or all badges in a style to unlock collections!')}</p>
                                </div>
                            ) : (
                                <div className="sets-list">
                                    {completedSets.map((set, idx) => (
                                        <div key={idx} className="set-item">
                                            <div className="set-icon">
                                                {set.type === 'badge_complete' ? '🎯' : '🎨'}
                                            </div>
                                            <div className="set-info">
                                                <h4>{set.name}</h4>
                                                <p>{set.description}</p>
                                            </div>
                                            <div className="set-count">{set.count}</div>
                                    </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserBadgesPage;